"use client";

import { useState, useCallback, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, X, Users, Hand } from "lucide-react";
import type { ResponseMode } from "@/lib/ai/responseModes";
import type { RaisedHand } from "@/lib/mindaloo/proactiveTriggers";
import VoiceInputButton, { type VoiceInputButtonHandle } from "@/components/chat/VoiceInputButton";
import AtlasNavChrome from "@/components/atlas/AtlasNavChrome";
import { haptics } from "@/lib/utils/haptics";
import { eventBus } from "@/lib/utils/eventBus";
import { consciousnessStore } from "@/lib/mindaloo/consciousnessStore";
import type { AiProfile } from "@/types/ai";
import type { V11StreamLine } from "@/lib/mindaloo/useKarmaV11Stream";

import type {
  AtlasNavigationResult,
  AtlasSearchProcess,
} from "@/lib/atlas/integration";

export interface ActiveTileInfo {
  id: string;
  name: string;
  icon?: string;
  type: string;
  profile: AiProfile | null;
  minimized?: boolean;
}

interface KarmaCommandBarProps {
  accent: string;
  /** Controlled input (Mindaloo shell). Omit for standalone internal state. */
  input?: string;
  onInputChange?: (value: string) => void;
  onSend?: (text: string) => void;
  loading?: boolean;
  onVoiceListeningChange?: (listening: boolean) => void;
  /** If provided, voice transcripts are sent here instead of the text composer. */
  onVoicePilot?: (text: string) => void;
  /** Fires when the user starts the microphone. */
  onVoiceStart?: () => void;
  /** Fires when the user explicitly stops the microphone. */
  onVoiceStop?: () => void;
  /** When set, enables Atlas strip above input (Mindaloo AC) inside this bar. */
  profile?: AiProfile | null;
  atlasProcess?: AtlasSearchProcess | null;
  atlasNavigationResult?: AtlasNavigationResult | null;
  atlasHistory?: Array<{
    process: AtlasSearchProcess;
    result: AtlasNavigationResult;
    messageId: string;
    timestamp: number;
  }>;
  activeTiles?: ActiveTileInfo[];
  v11Lines?: V11StreamLine[];
  v11Live?: boolean;
  responseMode?: ResponseMode;
  onResponseModeChange?: (mode: ResponseMode) => void;
  onAtlasOutcome?: (outcome: "accepted" | "corrected" | "rejected") => void;
  /** When true, microphone restarts automatically after Karma responds for hands-free conversation. */
  voiceContinuous?: boolean;
  /** External ref to the voice input button for imperative start/stop. */
  voiceInputRef?: React.RefObject<VoiceInputButtonHandle | null>;
  /** Live voice transcript turns to display in the Atlas strip. */
  voiceSession?: Array<{ role: "user" | "assistant"; content: string }>;
  /** Toggle the council / roundtable panel. */
  onCouncilToggle?: () => void;
  raisedHands?: RaisedHand[];
  onClearRaisedHand?: (triggerId: string) => void;
  /** True while the Council panel (which has its own composer) is open —
   *  both are fixed to the bottom of the screen, so showing this bar at the
   *  same time reads as two overlapping composers rather than a real clash
   *  to work around with z-index. */
  dimmed?: boolean;
}

export default function KarmaCommandBar({
  accent,
  input: controlledInput,
  onInputChange,
  onSend,
  loading = false,
  onVoiceListeningChange,
  onVoicePilot,
  onVoiceStart,
  onVoiceStop,
  profile = null,
  atlasProcess = null,
  atlasNavigationResult = null,
  atlasHistory = [],
  activeTiles = [],
  v11Lines = [],
  v11Live = false,
  responseMode = "auto",
  onResponseModeChange,
  onAtlasOutcome,
  voiceContinuous = false,
  voiceInputRef,
  voiceSession = [],
  onCouncilToggle,
  raisedHands = [],
  onClearRaisedHand,
  dimmed = false,
}: KarmaCommandBarProps) {
  const [voiceListening, setVoiceListening] = useState(false);
  const internalVoiceBtnRef = useRef<VoiceInputButtonHandle | null>(null);
  const [internalInput, setInternalInput] = useState("");
  const [modeOpen, setModeOpen] = useState(false);
  const modeRef = useRef<HTMLDivElement>(null);
  // The bar's unified pill container clips overflow (rounded corners around
  // the merged Atlas strip + input row), which was cutting into this dropdown
  // since it needs to pop up above that boundary. Portaled to <body> and
  // positioned from the trigger's real rect instead, so it draws over
  // everything rather than fighting the bar's own clipping.
  const [modePos, setModePos] = useState<{ bottom: number; right: number } | null>(null);
  useEffect(() => {
    if (!modeOpen || !modeRef.current) return;
    const rect = modeRef.current.getBoundingClientRect();
    setModePos({ bottom: window.innerHeight - rect.top + 8, right: window.innerWidth - rect.right });
  }, [modeOpen]);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const isControlled = controlledInput !== undefined && onInputChange !== undefined;
  const input = isControlled ? controlledInput : internalInput;
  const setInput = isControlled ? onInputChange! : setInternalInput;

  const sendText = useCallback((text: string) => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;
    if (onSend) onSend(trimmed);
    if (!isControlled) setInternalInput("");
    haptics.success();
  }, [loading, onSend, isControlled]);

  const submit = useCallback(() => {
    sendText(input);
  }, [input, sendText]);

  const handleVoiceInterim = useCallback((text: string) => {
    if (onVoicePilot) return; // pilot mode doesn't preview in composer
    if (isControlled) {
      onInputChange?.(text);
    } else {
      setInternalInput(text);
    }
  }, [isControlled, onInputChange, onVoicePilot]);

  const handleVoiceFinal = useCallback((text: string) => {
    if (onVoicePilot) {
      onVoicePilot(text);
      return;
    }
    if (isControlled) {
      onInputChange?.(text);
    } else {
      setInternalInput(text);
    }
    sendText(text);
  }, [isControlled, onInputChange, onVoicePilot, sendText]);

  // Was writing the error straight into the sendable input field — if the
  // user didn't notice and hit send, "Voice: Microphone permission denied..."
  // would go out as an actual chat message. A transient toast doesn't risk
  // getting submitted.
  const handleVoiceError = useCallback((error: string) => {
    eventBus.emit("TOAST:SHOW", { message: `Voice: ${error}`, type: "error" });
  }, []);

  const handleVoiceListeningChange = useCallback((listening: boolean) => {
    setVoiceListening(listening);
    onVoiceListeningChange?.(listening);
  }, [onVoiceListeningChange]);

  const setVoiceBtnRef = useCallback(
    (node: VoiceInputButtonHandle | null) => {
      internalVoiceBtnRef.current = node;
      if (voiceInputRef && typeof voiceInputRef === "object" && "current" in voiceInputRef) {
        (voiceInputRef as React.MutableRefObject<VoiceInputButtonHandle | null>).current = node;
      }
    },
    [voiceInputRef]
  );

  useEffect(() => {
    if (voiceContinuous) {
      internalVoiceBtnRef.current?.start();
    } else {
      internalVoiceBtnRef.current?.stop();
    }
  }, [voiceContinuous]);


  const MODES: { key: ResponseMode; label: string; icon: string }[] = [
    { key: "auto",           label: "Auto",           icon: "⚡" },
    { key: "quick",          label: "Quick response",  icon: "💬" },
    { key: "think_deeper",   label: "Think Deeper",    icon: "🧠" },
    { key: "smart",          label: "Smart",           icon: "✨" },
    { key: "study",          label: "Study and learn", icon: "📚" },
    { key: "deep_research",  label: "Deep Research",   icon: "🔬" },
    { key: "search",         label: "Search",          icon: "🔍" },
    { key: "structured",     label: "Structured",      icon: "📋" },
  ];
  const currentMode = MODES.find(m => m.key === responseMode) ?? MODES[0];

  const primaryColor = profile?.brand?.primaryColor || accent;
  const secondaryColor = profile?.brand?.secondaryColor || "#8b5cf6";
  const barTextColor = "rgba(255, 255, 255, 0.92)";

  const [cScore, setCScore] = useState(consciousnessStore.score);
  useEffect(() => consciousnessStore.subscribe(setCScore), []);
  // 0–1 glow intensity derived from C-score
  const cNorm = cScore / 100;
  const glowAlpha = 0.10 + cNorm * 0.32;
  const glowSize  = 8 + cNorm * 22;

  return (
    <motion.div
      className="karma-chatbar-wrap karma-chatbar-unified"
      initial={{ y: 80, opacity: 0 }}
      animate={{ y: 0, opacity: dimmed ? 0 : 1 }}
      transition={{ type: "spring", stiffness: 280, damping: 28 }}
      style={{
        filter: `drop-shadow(0 0 ${glowSize}px rgba(193,162,255,${glowAlpha}))`,
        pointerEvents: dimmed ? "none" : "auto",
      }}
    >
      {/* Atlas strip sits above the pill bar */}
      {profile && (
        <AtlasNavChrome
          profile={profile}
          atlasProcess={atlasProcess}
          atlasNavigationResult={atlasNavigationResult}
          atlasHistory={atlasHistory}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          barTextColor={barTextColor}
          activeTiles={activeTiles}
          v11Lines={v11Lines}
          v11Live={v11Live}
          voiceListening={voiceListening}
          voiceSession={voiceSession}
          onOutcome={onAtlasOutcome}
        >
          {null}
        </AtlasNavChrome>
      )}

      {/* Single horizontal pill. Used to open with the Karma Orb (tap/hold
          deck launcher) and the Smart Dial (thought-stream/memory/shelf
          dots) — both parked. Every destination they offered now lives in
          one place, the Neural Shelf (opened from the edge tab), instead of
          three separate ways to reach the same handful of things. */}
      <div className="karma-chatbar karma-glass karma-neon-ring">
        {/* Input */}
        <textarea
          ref={inputRef}
          rows={1}
          className="karma-chat-input karma-command-composer flex-1 h-11 max-h-11 resize-none overflow-y-auto"
          placeholder="Command your autonomous consciousness…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
            if ((e.metaKey || e.ctrlKey) && e.key === "k") {
              e.preventDefault();
              eventBus.emit("COMMAND_PALETTE:OPEN");
            }
          }}
          disabled={loading}
          aria-label="Command input"
        />

        {/* Council / Roundtable */}
        {activeTiles.length > 1 && onCouncilToggle && (
          <button
            type="button"
            onClick={onCouncilToggle}
            className="shrink-0 p-2 rounded-lg transition hover:bg-white/10 relative"
            style={{ color: accent }}
            title="Open council"
            aria-label="Open council"
          >
            <Users className="w-4 h-4" />
            {raisedHands.length > 0 && (
              <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-rose-400" />
            )}
          </button>
        )}

        {/* Raised hands */}
        {raisedHands.length > 0 && (
          <div className="shrink-0 flex items-center gap-1 -mr-1">
            {raisedHands.slice(0, 3).map((hand) => (
              <button
                key={hand.triggerId}
                type="button"
                onClick={() => {
                  eventBus.emit("SPATIAL:PROACTIVE_SPEAK", { tileId: hand.tileId, message: hand.message });
                  onClearRaisedHand?.(hand.triggerId);
                }}
                className="flex items-center gap-1 px-2 py-1 rounded-full text-[10px] bg-white/10 text-white/80 hover:bg-white/15 transition animate-pulse"
                title={hand.reason}
              >
                <Hand className="w-3 h-3" style={{ color: accent }} />
                <span className="truncate max-w-[80px]">{hand.profileName}</span>
              </button>
            ))}
            {raisedHands.length > 3 && (
              <button
                type="button"
                onClick={onCouncilToggle}
                className="text-[10px] text-white/50 hover:text-white/80 transition"
              >
                +{raisedHands.length - 3}
              </button>
            )}
          </div>
        )}

        {/* Mic morphs into Send the moment there's text to send — mirrors
            iMessage/WhatsApp/ChatGPT mobile, and removes a whole button that
            used to sit here permanently regardless of whether there was
            anything to act on. Voice control takes priority while actually
            listening, even if interim text has started appearing, so the
            control you're using doesn't change out from under you mid-turn. */}
        {voiceListening || !input.trim() ? (
          <VoiceInputButton
            ref={setVoiceBtnRef}
            autoRestart={voiceContinuous}
            onInterimTranscript={handleVoiceInterim}
            onFinalTranscript={handleVoiceFinal}
            onListeningChange={handleVoiceListeningChange}
            onError={handleVoiceError}
            onVoiceStart={onVoiceStart}
            onVoiceStop={() => {
              setVoiceListening(false);
              onVoiceListeningChange?.(false);
              onVoiceStop?.();
            }}
            color={accent}
            className="karma-voice-btn shrink-0"
          />
        ) : (
          <button
            type="button"
            className="karma-send-btn shrink-0"
            disabled={loading}
            onClick={submit}
            aria-label="Send"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" style={{ color: accent }} />
            ) : (
              "Send"
            )}
          </button>
        )}

        {/* Response mode selector — icon-only by default (was icon + visible
            label + chevron, permanently taking up label-width even though
            the label only mattered once you'd already opened the picker). */}
        <div ref={modeRef} className="relative shrink-0">
          <button
            type="button"
            className="flex items-center justify-center w-8 h-8 rounded-full text-sm transition-all select-none"
            style={{
              background: modeOpen ? `${accent}20` : "rgba(255,255,255,0.05)",
              border: `1px solid ${modeOpen ? accent + "50" : "rgba(255,255,255,0.1)"}`,
              color: modeOpen ? accent : "rgba(255,255,255,0.55)",
            }}
            onClick={() => setModeOpen(o => !o)}
            title={currentMode.label}
            aria-label={`Response mode: ${currentMode.label}`}
          >
            <span>{currentMode.icon}</span>
          </button>

          {typeof document !== "undefined" && modePos && createPortal(
            <AnimatePresence>
              {modeOpen && (
                <motion.div
                  key="mode-dropdown"
                  className="fixed rounded-2xl overflow-hidden"
                  style={{
                    bottom: modePos.bottom,
                    right: modePos.right,
                    background: "rgba(10,10,22,0.97)",
                    backdropFilter: "blur(24px)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    boxShadow: "0 8px 40px rgba(0,0,0,0.55)",
                    minWidth: 180,
                    zIndex: 99999,
                  }}
                  initial={{ opacity: 0, y: 6, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 6, scale: 0.96 }}
                  transition={{ duration: 0.15 }}
                >
                  {/* Close row */}
                  <div className="flex items-center justify-between px-3 pt-2.5 pb-1.5" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                    <span className="text-[10px] uppercase tracking-widest text-white/30">Mode</span>
                    <button type="button" onClick={() => setModeOpen(false)} className="text-white/30 hover:text-white/60 transition-colors">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                  {MODES.map(mode => (
                    <button
                      key={mode.key}
                      type="button"
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-[12px] transition-colors"
                      style={{
                        background: responseMode === mode.key ? `${accent}15` : "transparent",
                        color: responseMode === mode.key ? accent : "rgba(255,255,255,0.65)",
                      }}
                      onClick={() => {
                        onResponseModeChange?.(mode.key);
                        setModeOpen(false);
                      }}
                    >
                      <span className="text-base w-5 text-center shrink-0">{mode.icon}</span>
                      {mode.label}
                      {responseMode === mode.key && <span className="ml-auto text-[10px]" style={{ color: accent }}>✓</span>}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>,
            document.body
          )}
        </div>
      </div>
    </motion.div>
  );
}
